import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Plus, Search, Users, Phone, Mail, Send } from "lucide-react";
import { ClientInviteDialog } from "@/components/clients/ClientInviteDialog";

const emptyForm = { name: "", mobile: "", email: "", address: "", notes: "" };

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/clients", { params: { search: search || undefined } });
    setClients(data);
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client added");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle="Everyone you represent, in one directory."
        actions={<Button onClick={() => setOpen(true)} data-testid="add-client-btn" className="bg-gold text-navy hover:bg-gold-dark"><Plus className="mr-1.5 h-4 w-4" /> Add Client</Button>} />

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="clients-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, mobile or email…" className="pl-9" />
      </div>

      {!clients ? <LoadingState /> : clients.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" description="Add your first client to link them to cases." testid="clients-empty"
          action={<Button onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-light"><Plus className="mr-1.5 h-4 w-4" /> Add Client</Button>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {clients.map((c) => (
            <Card key={c.id} data-testid={`client-card-${c.id}`} onClick={() => navigate(`/clients/${c.id}`)} className="cursor-pointer border-border p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold">
              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11"><AvatarFallback className="bg-navy text-sm text-white">{c.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-navy">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.client_code} · {c.case_count} case{c.case_count === 1 ? "" : "s"}</p>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {c.mobile && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {c.mobile}</p>}
                {c.email && <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5" /> {c.email}</p>}
              </div>
              <Button variant="outline" size="sm" data-testid={`client-invite-btn-${c.id}`}
                      onClick={(e) => { e.stopPropagation(); setInviteFor(c); }}
                      className="mt-4 w-full border-border text-navy hover:border-gold hover:bg-secondary">
                <Send className="mr-1.5 h-3.5 w-3.5" /> Invite to portal
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Add Client</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Full Name *</Label><Input data-testid="client-name-input" value={form.name} onChange={set("name")} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Mobile</Label><Input data-testid="client-mobile-input" value={form.mobile} onChange={set("mobile")} /></div>
              <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email} onChange={set("email")} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Address</Label><Input value={form.address} onChange={set("address")} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="client-save-btn" className="bg-navy text-white hover:bg-navy-light">{saving ? "Saving…" : "Add Client"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {inviteFor && (
        <ClientInviteDialog client={inviteFor} open={!!inviteFor}
                            onOpenChange={(v) => !v && setInviteFor(null)} />
      )}
    </div>
  );
}
